import { createActionBarEffect } from '@databases/action-bar/selections-effect';
import { collabStateManager } from '@databases/collab-state-manager';
import { createContextMenuEffect } from '@databases/context-menu/context-menu-effect';
import {
  getCachedViewSnapshotForSelections,
  getVersionId,
} from '@databases/database-state';
import { createViewStateSubscription } from '@databases/database-state/state/view';
import { key, notify } from '@databases/ping-registry';
import { type PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { LegacyReduxSelectionsReader } from '@databases/redux/cellSelections/legacy-selections-reader';
import cellSelectionsSlice from '@databases/redux/cellSelections/slice';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { doClearValuesWithIds } from '@databases/redux/contextual-menu/actions';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { appStore } from '@databases/redux/store';
import { doFillValues } from '@databases/redux/table-layout/actions';
import { ReduxDatabaseAdapter } from '@databases/redux-database-adapter';
import { createPublishRovoSelectionEffect } from '@databases/rovo-bridge/selections-effect';
import {
  createApplyFillEffect,
  createClearCellsEffect,
  createCheckboxEffect,
  createPresenceEffect,
  createViewChangesInput,
  createYjsChangesInput,
  initializeSelectionsMachine,
  type SelectionsMachine,
} from '@databases/selections';
import {
  LegacyReduxAdapter,
  SelectionsApi,
  SelectionsApiAdapter,
  SelectionsApiReader,
} from '@databases/selections-api';
import { container, getService } from '@databases/service-container';
import { createCellEditingEffect } from '@databases/value/cell-edit-effect';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';

import { createCollabStructuralChangeSubscription } from './create-collab-structural-change-subscription';
import { type Recyclable } from './types';

function registerLegacySelectionsDependencies(contentId: string) {
  container.registerSingleton(
    'SelectionsActions',
    () =>
      new LegacyReduxAdapter({
        contentId,
        dispatch: appStore.dispatch,
        sliceActions: cellSelectionsSlice.actions,
      })
  );

  const getState = appStore.getState;
  const getContentId = () => contentId;
  const getDatabaseSnapshot = () => {
    const database = getDatabaseWithCurrentView(getState(), { contentId });
    return {
      entries: database.entries,
      fields: database.fields,
    };
  };

  container.registerSingleton(
    'SelectionsReader',
    () =>
      new LegacyReduxSelectionsReader({
        getState,
        getContentId,
        getDatabaseSnapshot,
      })
  );
}

function initializeLegacySelectionsRecyclable(): Recyclable {
  return {
    setup(contentId) {
      registerLegacySelectionsDependencies(contentId);
    },
    dispose() {},
  };
}

function initializeSelectionsMachineRecyclable(): Recyclable {
  let destroy: VoidFunction;

  function initialize(currentContentId: string) {
    const collabManager = collabStateManager.getCollabManager(currentContentId);

    // Get the machine and the setup function first, without providing inputs/effects yet.
    const { setup, machine } = initializeSelectionsMachine({
      highlightsController: getService('TableHighlightsController'),
    });

    const databaseAdapter = new ReduxDatabaseAdapter({
      getContentId: () => currentContentId,
      getDatabaseSnapshot: (id) =>
        selectDatabaseDraftByContentId(appStore.getState(), {
          contentId: id,
          includeDeleted: false,
          viewSettings: getCachedViewSnapshotForSelections().settings,
        }),
      getVersionId,
      getState: appStore.getState,
      pluginChecker: {
        isEnabled: (key) => isPluginEnabled(key as PluginKey),
      },
    });

    const selectionsApi = new SelectionsApi({ machine, adapter: databaseAdapter });

    // Now that we have concrete instances, register them immediately.
    container.registerSingleton('SelectionsApi', () => selectionsApi);
    container.registerSingleton('SelectionsActions', () => {
      return new SelectionsApiAdapter({ selectionsApi });
    });
    container.registerSingleton('SelectionsReader', () => {
      return new SelectionsApiReader({
        selectionsApi,
        adapter: databaseAdapter,
      });
    });

    // Build inputs and effects using the fully initialized instances.
    const inputs = [
      (m: SelectionsMachine) => createViewChangesInput(m, createViewStateSubscription),
    ];

    if (collabManager) {
      inputs.push((m: SelectionsMachine) =>
        createYjsChangesInput(m, createCollabStructuralChangeSubscription(collabManager))
      );
    }

    const effects = [
      () =>
        createActionBarEffect({
          selectionsApi,
          contentId: currentContentId,
          databaseAdapter,
          panelManager: getService('PanelManager'),
        }),
      () =>
        createContextMenuEffect({
          selectionsApi,
          databaseAdapter,
          contentId: currentContentId,
          formatMessage: getService('Intl').formatMessage,
          panelManager: getService('PanelManager'),
          selectionsActions: getService('SelectionsActions'),
          currentViewId: getCachedViewSnapshotForSelections().id,
          highlightsController: getService('TableHighlightsController'),
          selectionsReader: getService('SelectionsReader'),
        }),
      /**
       * This effect will NOT be included when a user cannot edit
       */
      () =>
        createCellEditingEffect({
          selectionsApi,
          notifyCellEditing: (entryId, fieldId, editing) => {
            const cellKey = key('cell', { entryId, fieldId });

            notify(cellKey, 'editing', editing);
          },
        }),
      () =>
        createPublishRovoSelectionEffect({
          selectionsApi,
          publishSelectionToRovo: (selection) => {
            waitForContentAPI().then((contentAPI) => {
              contentAPI?.publishDatabaseReferencingContent?.(selection);
            });
          },
          currentViewType: getCachedViewSnapshotForSelections().settings.layout.type,
        }),
      () =>
        createPresenceEffect({
          selectionsApi,
          collabStateManager,
          contentId: currentContentId,
        }),
      () =>
        createCheckboxEffect({
          databaseAdapter,
          notifyRowSelected: (entryId: string, selected: boolean) => {
            const rowKey = key('row', { entryId });

            notify(rowKey, 'selected', selected);
          },
          notifyHeaderCheckboxState: (state) => {
            const headerKey = key('headerCheckbox', {});

            notify(headerKey, 'state', state);
          },
        }),
      () =>
        createApplyFillEffect({
          resolveRangeToIds: (range) => selectionsApi.resolveRangeToIds(range),
          onFillValues: ({
            fillEntryIds,
            fillFieldIds,
            sourceEntryIds,
            sourceFieldIds,
          }) => {
            appStore.dispatch(
              doFillValues(
                currentContentId,
                fillFieldIds,
                fillEntryIds,
                sourceEntryIds,
                sourceFieldIds
              )
            );
          },
          onSelectFilledValues: (filledValues) => {
            selectionsApi.selectValues(filledValues);
          },
        }),
      () =>
        createClearCellsEffect({
          resolveRangeToIds: (range) => selectionsApi.resolveRangeToIds(range),
          onClearValues: (entryIds, fieldIds) => {
            appStore.dispatch(doClearValuesWithIds(currentContentId, entryIds, fieldIds));
          },
        }),
    ];

    // Pass the configurations to the setup function to start the machine.
    destroy = setup(inputs, effects);
  }

  return {
    setup(contentId) {
      initialize(contentId);
    },

    dispose() {
      // Optional chaining just in case dispose is called before setup finishes
      destroy?.();
    },
  };
}

/**
 * Initializes the selections machine behind a feature gate
 * with a listener on databaseContentIdChanged to destroy
 * and rebuild it, so no highlights or state exists between
 * changing the content id.
 */
export function initializeRecyclableSelectionsMachine(): Recyclable {
  const experimentIsEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );

  return experimentIsEnabled
    ? initializeSelectionsMachineRecyclable()
    : initializeLegacySelectionsRecyclable();
}
